<script setup>
import { computed, h, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { formatTime } from '@chatwoot/utils';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import OverviewReportFilters from './OverviewReportFilters.vue';
import CompanyVolumeChart from './CompanyVolumeChart.vue';
import SummaryReportLink from './SummaryReportLink.vue';
import Table from 'dashboard/components/table/Table.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import MetricHint from './MetricHint.vue';
import { generateFileName } from 'dashboard/helper/downloadHelper';

const store = useStore();
const { t } = useI18n();
const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const columnHelper = createColumnHelper();

const uiFlags = useMapGetter('summaryReports/getUIFlags');
const reportRows = useMapGetter('summaryReports/getCompanySummaryReports');
const isLoading = computed(
  () => uiFlags.value.isFetchingCompanySummaryReports ?? false
);

const renderAvgTime = value => (value ? formatTime(value) : '--');
const renderCount = value =>
  value || value === 0 ? Number(value).toLocaleString() : '--';
const renderActivity = value =>
  value ? shortTimestamp(dynamicTime(value), true) : '--';

const metricHeader = (labelKey, tooltipKey) => () =>
  h(MetricHint, {
    label: t(labelKey),
    tooltip: t(tooltipKey),
  });

const companyNameCell = cellProps => {
  if (!cellProps.row.original.id) {
    return h(MetricHint, {
      label: cellProps.getValue(),
      tooltip: t('COMPANY_REPORTS.TOOLTIPS.NO_COMPANY'),
      muted: true,
    });
  }
  return h(SummaryReportLink, cellProps);
};

const columns = computed(() => [
  columnHelper.accessor('name', {
    header: t('SUMMARY_REPORTS.COMPANY'),
    width: 240,
    cell: companyNameCell,
  }),
  columnHelper.accessor('conversationsCount', {
    header: metricHeader(
      'COMPANY_REPORTS.METRICS.CONVERSATIONS',
      'COMPANY_REPORTS.TOOLTIPS.CONVERSATIONS'
    ),
    width: 160,
  }),
  columnHelper.accessor('openConversationsCount', {
    header: metricHeader(
      'COMPANY_REPORTS.METRICS.OPEN',
      'COMPANY_REPORTS.TOOLTIPS.OPEN'
    ),
    width: 140,
  }),
  columnHelper.accessor('resolvedConversationsCount', {
    header: metricHeader(
      'COMPANY_REPORTS.METRICS.RESOLVED',
      'COMPANY_REPORTS.TOOLTIPS.RESOLVED'
    ),
    width: 140,
  }),
  columnHelper.accessor('avgFirstResponseTime', {
    header: metricHeader(
      'COMPANY_REPORTS.METRICS.FIRST_RESPONSE_TIME',
      'COMPANY_REPORTS.TOOLTIPS.FIRST_RESPONSE_TIME'
    ),
    width: 180,
  }),
  columnHelper.accessor('avgResolutionTime', {
    header: metricHeader(
      'COMPANY_REPORTS.METRICS.RESOLUTION_TIME',
      'COMPANY_REPORTS.TOOLTIPS.RESOLUTION_TIME'
    ),
    width: 180,
  }),
  columnHelper.accessor('lastActivityAt', {
    header: metricHeader(
      'COMPANY_REPORTS.METRICS.LAST_ACTIVITY',
      'COMPANY_REPORTS.TOOLTIPS.LAST_ACTIVITY'
    ),
    width: 180,
  }),
]);

const tableData = computed(() =>
  (reportRows.value || []).map(row => ({
    id: row.id,
    name: row.name || t('COMPANY_REPORTS.NO_COMPANY'),
    type: 'company',
    conversationsCount: renderCount(row.conversationsCount),
    openConversationsCount: renderCount(row.openConversationsCount),
    resolvedConversationsCount: renderCount(row.resolvedConversationsCount),
    avgFirstResponseTime: renderAvgTime(row.avgFirstResponseTime),
    avgResolutionTime: renderAvgTime(row.avgResolutionTime),
    lastActivityAt: renderActivity(row.lastActivityAt),
  }))
);

const chartRows = computed(() =>
  (reportRows.value || []).map(row => ({
    id: row.id,
    name: row.name || t('COMPANY_REPORTS.NO_COMPANY'),
    conversationsCount: row.conversationsCount,
  }))
);

const fetchReports = async () => {
  const params = {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
  };
  try {
    await store.dispatch('summaryReports/fetchCompanySummaryReports', params);
  } catch {
    useAlert(t('REPORT.SUMMARY_FETCHING_FAILED'));
  }
};

const onFilterChange = updatedFilter => {
  from.value = updatedFilter.from;
  to.value = updatedFilter.to;
  businessHours.value = updatedFilter.businessHours;
  fetchReports();
};

onMounted(() => {
  if (from.value && to.value) fetchReports();
});

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

const downloadReports = () => {
  const fileName = generateFileName({
    type: 'company',
    to: to.value,
    businessHours: businessHours.value,
  });
  store.dispatch('downloadCompanyReports', {
    from: from.value,
    to: to.value,
    fileName,
    businessHours: businessHours.value,
  });
};

defineExpose({ downloadReports });
</script>

<template>
  <OverviewReportFilters
    :disabled="isLoading"
    :business-hours-tooltip="$t('COMPANY_REPORTS.TOOLTIPS.BUSINESS_HOURS')"
    :business-hours-label="$t('COMPANY_REPORTS.BUSINESS_HOURS_LABEL')"
    @filter-change="onFilterChange"
  />
  <div class="relative">
    <div :class="{ 'opacity-50': isLoading }">
      <CompanyVolumeChart :rows="chartRows" />
      <div
        class="flex-1 overflow-auto px-2 py-2 mt-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
      >
        <p
          v-if="!isLoading && !tableData.length"
          class="px-4 py-10 text-sm text-center text-n-slate-11"
        >
          {{ $t('COMPANY_REPORTS.NO_RECORDS') }}
        </p>
        <Table v-else :table="table" />
      </div>
    </div>
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      leave-active-class="transition-opacity duration-200 ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isLoading"
        class="absolute inset-0 z-10 flex items-center justify-center"
      >
        <Spinner :size="32" class="text-n-brand" />
      </div>
    </Transition>
  </div>
</template>
